import type { SnackBarConfig } from '@shared/components/Snackbar';
import type { Reaction } from '@shared/domain/reaction/Reaction';
import { Box, Button, FormControl, Typography } from '@mui/material';
import React, { useContext, useMemo, useRef } from 'react';
import { Textarea } from '@shared/components/TextArea';
import { UserContext } from '@/shared/contexts/UserContext';
import { ReactionSubjectType, ReactionType } from '@shared/domain/reaction/Reaction';
import { useApi } from '@shared/services/api';
import { useMessageService } from '@/shared/services/MessageService/MessageServiceContext';
import { InfoContainer } from '../styles/ReviewReader.styles';

interface ReactionInputsProps {
  reviewId: string | number;
  reactions: Reaction[];
  onReactionUpdate: () => void;
  onReactionCountUpdate?: (reactionType: ReactionType, isAdding: boolean) => void;
  onSnackbarUpdate: (config: SnackBarConfig | null) => void;
}

export default function ReactionInputs({
  reviewId,
  reactions,
  onReactionUpdate,
  onReactionCountUpdate,
  onSnackbarUpdate,
}: ReactionInputsProps) {
  const { user } = useContext(UserContext);
  const { openMessage, closeMessage } = useMessageService();
  const api = useApi();
  const reportCommentRef = useRef<HTMLTextAreaElement>(null);

  const handleLikeDislike = async (reactionType: ReactionType) => {
    const existingReaction = reactions.find(
      reaction =>
        (reaction.reactionType === ReactionType.Like || reaction.reactionType === ReactionType.Dislike)
        && reaction.userId === user?.id,
    );

    try {
      const _reaction = await api.post('/reactions/toggle', {
        reactionType,
        subjectId: reviewId,
        subjectType: ReactionSubjectType.Review,
      });

      // Update reaction counts locally
      if (onReactionCountUpdate) {
        if (existingReaction) {
          // Removing existing reaction
          onReactionCountUpdate(existingReaction.reactionType, false);
          // Adding new reaction if different
          if (existingReaction.reactionType !== reactionType) {
            onReactionCountUpdate(reactionType, true);
          }
        }
        else {
          // Adding new reaction
          onReactionCountUpdate(reactionType, true);
        }
      }

      onReactionUpdate();
    }
    catch (error) {
      console.error('Error toggling reaction:', error);
      onSnackbarUpdate({
        type: 'error',
        message: 'Failed to update your reaction. Please try again.',
        open: true,
      });
    }
  };

  const handleSubmitReport = async () => {
    const comment = reportCommentRef.current?.value || '';
    if (!comment.trim()) {
      onSnackbarUpdate({
        type: 'error',
        message: 'Please provide a reason for reporting this review.',
        open: true,
      });
      return;
    }

    try {
      await api.post('/reactions', {
        reactionType: ReactionType.Report,
        subjectId: reviewId,
        subjectType: ReactionSubjectType.Review,
        comment,
      });
      onSnackbarUpdate({
        type: 'success',
        message: 'Thank you for reporting this review.',
        open: true,
      });
      onReactionUpdate();
      closeMessage();
    }
    catch (error) {
      console.error('Error submitting report:', error);
      onSnackbarUpdate({
        type: 'error',
        message: 'Failed to submit your report. Please try again.',
        open: true,
      });
    }
  };

  const isLiked = useMemo(() => {
    return reactions.some(
      reaction =>
        reaction.reactionType === ReactionType.Like
        && reaction.userId === user?.id,
    );
  }, [reactions, user]);

  const isDisliked = useMemo(() => {
    return reactions.some(
      reaction =>
        reaction.reactionType === ReactionType.Dislike
        && reaction.userId === user?.id,
    );
  }, [reactions, user]);

  const isReported = useMemo(() => {
    return reactions.some(
      reaction =>
        reaction.reactionType === ReactionType.Report
        && reaction.userId === user?.id,
    );
  }, [reactions, user]);

  const handleReport = async () => {
    openMessage({
      modalType: 'action',
      customBody: (
        <Box>
          {isReported && (
            <Typography sx={{ mb: 2 }} color="warning.main">
              ⚠️ You have already reported this review.
            </Typography>
          )}
          <Typography variant="h5" gutterBottom>
            Report this review
          </Typography>
          <Typography sx={{ mb: 2 }}>
            Please tell us why you want to report this review.
          </Typography>
          <FormControl fullWidth>
            <Textarea
              ref={reportCommentRef}
              minRows={3}
              placeholder="Please describe why you're reporting this deal retro..."
            />
          </FormControl>
          <Button sx={{ mt: 2, backgroundColor: 'var(--color-brand-primary)', color: 'white' }} variant="contained" onClick={handleSubmitReport}>Submit</Button>
        </Box>
      ),
      size: 'medium',
    });
  };

  return (
    <Box className="reaction-inputs" sx={{ margin: '0 var(--spacing-md) var(--spacing-md) var(--spacing-md) var(--spacing-md)' }}>
      <InfoContainer>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6" gutterBottom>
            React to this deal retro:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
            <Button variant="outlined" sx={{ height: '40px', fontSize: '1.6em', background: isLiked ? 'var(--color-darker-blue)' : 'inherit' }} onClick={() => handleLikeDislike(ReactionType.Like)}>👍</Button>
            <Button variant="outlined" sx={{ height: '40px', fontSize: '1.6em', background: isDisliked ? 'var(--color-darker-blue)' : 'inherit' }} onClick={() => handleLikeDislike(ReactionType.Dislike)}>👎</Button>
            <Button variant="outlined" sx={{ height: '40px', fontSize: '1em', background: isReported ? 'var(--color-darker-blue)' : 'inherit', color: isReported ? 'white' : 'inherit' }} onClick={handleReport}>Report</Button>
          </Box>
        </Box>
      </InfoContainer>
    </Box>
  );
}
